import { createAsyncThunk, createSlice, createSelector } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { dashboardAPI as apiConfig } from '../../../axiosConfig';

const SLICE_NAME = 'paymentOptionsProcessingFees';
const URL = 'payment-options-proxy?endpoint=paymentdashboard/processingfees';

export const addProcessingFee = createAsyncThunk(
  `${SLICE_NAME}/add`,
  async (payload) => {
    const response = await apiConfig.post(URL, payload).catch((error) => {
      console.error(error);
      return error?.response;
    });
    return response?.data;
  },
);

export const updateProcessingFee = createAsyncThunk(
  `${SLICE_NAME}/update`,
  async ({ id, payload }) => {
    const response = await apiConfig
      .put(`${URL}/${id}`, payload)
      .catch((error) => {
        console.error(error);
        return error?.response;
      });
    return response?.data;
  },
);

export const deleteProcessingFee = createAsyncThunk(
  `${SLICE_NAME}/delete`,
  async (id) => {
    const response = await apiConfig.delete(`${URL}/${id}`).catch((error) => {
      console.error(error);
      return error?.response;
    });
    return response?.data;
  },
);

const initialState = {
  processingFeesList: [],
  filteredLoB: [],
  filteredCurrency: [],
  filteredMethodCode: [],
  filteredStartDate: null,
  filteredEndDate: null,
};

const paymentOptionsProcessingFeesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    resetFilters(state) {
      state.filteredLoB = [];
      state.filteredCurrency = [];
      state.filteredMethodCode = [];
      state.filteredStartDate = null;
      state.filteredEndDate = null;
    },
    setFilterLoB(state, { payload }) {
      state.filteredLoB = payload;
    },
    setFilterCurrency(state, { payload }) {
      state.filteredCurrency = payload;
    },
    setFilterMethodCode(state, { payload }) {
      state.filteredMethodCode = payload;
    },
    setFilterStartDate(state, { payload }) {
      state.filteredStartDate = payload;
    },
    setFilterEndDate(state, { payload }) {
      state.filteredEndDate = payload;
    },
    setProcessingFeesList(state, { payload }) {
      state.processingFeesList = payload;
    },

    removeFilterLoB(state, { payload }) {
      const existingLoB = state.filteredLoB;
      const newFilteredLoB = existingLoB.filter((lob) => lob !== payload);
      state.filteredLoB = newFilteredLoB;
    },
    removeFilterCurrency(state, { payload }) {
      const existingCurrency = state.filteredCurrency;
      const newFilteredCurrency = existingCurrency.filter(
        (currency) => currency !== payload,
      );
      state.filteredCurrency = newFilteredCurrency;
    },
    removeFilterMethodCode(state, { payload }) {
      const existingMethodCode = state.filteredMethodCode;
      const newFilteredMethodCode = existingMethodCode.filter(
        (methodCode) => methodCode !== payload,
      );
      state.filteredMethodCode = newFilteredMethodCode;
    },
    removeFilterStartEndDate(state) {
      state.filteredStartDate = null;
      state.filteredEndDate = null;
    },
  },
});

export const paymentOptionsProcessingFeesActions =
  paymentOptionsProcessingFeesSlice.actions;

export const selectProcessingFeeList = (state) =>
  state.paymentOptions.processingFees.processingFeesList;

export const selectProcessingFeeFilteredLoB = (state) =>
  state.paymentOptions.processingFees.filteredLoB;

export const selectProcessingFeeFilteredCurrency = (state) =>
  state.paymentOptions.processingFees.filteredCurrency;

export const selectProcessingFeeFilteredMethodCode = (state) =>
  state.paymentOptions.processingFees.filteredMethodCode;

export const selectProcessingFeeFilteredStartDate = (state) =>
  state.paymentOptions.processingFees.filteredStartDate;

export const selectProcessingFeeFilteredEndDate = (state) =>
  state.paymentOptions.processingFees.filteredEndDate;

export const selectAvailableProcessingFeeLoB = (state) => [
  ...new Set(
    state.paymentOptions.processingFees.processingFeesList.map(
      ({ paymentMode }) => paymentMode,
    ),
  ),
];

export const selectAvailableProcessingFeeCurrency = (state) => [
  ...new Set(
    state.paymentOptions.processingFees.processingFeesList.map(
      ({ currency }) => currency,
    ),
  ),
];

export const selectAvailableProcessingFeeMethodCode = (state) => [
  ...new Set(
    state.paymentOptions.processingFees.processingFeesList.map(
      ({ methodCode }) => methodCode,
    ),
  ),
];

export const selectProcessingFeeFilteredList = createSelector(
  selectProcessingFeeList,
  selectProcessingFeeFilteredLoB,
  selectProcessingFeeFilteredCurrency,
  selectProcessingFeeFilteredMethodCode,
  selectProcessingFeeFilteredStartDate,
  selectProcessingFeeFilteredEndDate,
  (
    processingFees,
    filteredLoB,
    filteredCurrency,
    filteredMethodCode,
    filteredStartDate,
    filteredEndDate,
  ) => {
    let filteredProcessingFees = processingFees;

    if (filteredLoB && filteredLoB.length !== 0) {
      filteredProcessingFees = filteredProcessingFees.filter(
        ({ paymentMode }) => filteredLoB.includes(paymentMode),
      );
    }

    if (filteredCurrency && filteredCurrency.length !== 0) {
      filteredProcessingFees = filteredProcessingFees.filter(({ currency }) =>
        filteredCurrency.includes(currency),
      );
    }

    if (filteredMethodCode && filteredMethodCode.length !== 0) {
      filteredProcessingFees = filteredProcessingFees.filter(({ methodCode }) =>
        filteredMethodCode.includes(methodCode),
      );
    }

    if (filteredStartDate || filteredEndDate) {
      filteredProcessingFees = filteredProcessingFees.filter(
        ({ id, createdAt, updatedAt }) => {
          const isStartDateInFilterRange = dayjs(createdAt).isBetween(
            filteredStartDate,
            filteredEndDate,
          );

          const isEndDateInFilterRange = dayjs(updatedAt).isBetween(
            filteredStartDate,
            filteredEndDate,
          );

          const isFilterStartDateInRuleDateRange = dayjs(
            filteredStartDate,
          ).isBetween(createdAt, updatedAt);

          const isFilterEndDateInRuleDateRange = dayjs(
            filteredEndDate,
          ).isBetween(createdAt, updatedAt);

          return (
            isStartDateInFilterRange ||
            isEndDateInFilterRange ||
            isFilterStartDateInRuleDateRange ||
            isFilterEndDateInRuleDateRange
          );
        },
      );
    }

    return filteredProcessingFees;
  },
);

export default paymentOptionsProcessingFeesSlice.reducer;
